/**
 * Voice search
 * Adds "search by voice" support (like Google's mic icon) to any search form
 * that contains a button with [data-voice-search-button] next to its
 * text input. Uses the native Web Speech API, so no external dependency
 * is required. If the browser does not support it, the button is hidden
 * automatically via the "no-voice-search" class added to <html>.
 */
(() => {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

  if (!SpeechRecognition) {
    document.documentElement.classList.add('no-voice-search');
    return;
  }

  let activeRecognition = null;
  let activeButton = null;

  const getLang = () => document.documentElement.getAttribute('lang') || 'pt-BR';

  const stopActive = () => {
    if (activeRecognition) {
      activeRecognition.stop();
    }
  };

  const onButtonClick = (event) => {
    const button = event.target.closest('[data-voice-search-button]');

    if (!button) return;

    event.preventDefault();

    // Clicking the button again while listening stops it
    if (button === activeButton) {
      stopActive();
      return;
    }

    // Only one recognition session at a time
    stopActive();

    const form = button.closest('form');
    const input = form ? form.querySelector('input[type="search"], input[name="q"]') : null;

    if (!input) return;

    const recognition = new SpeechRecognition();
    recognition.lang = getLang();
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    activeRecognition = recognition;
    activeButton = button;

    button.classList.add('is-listening');
    button.setAttribute('aria-pressed', 'true');
    form.classList.add('is-listening');

    recognition.addEventListener('result', (recognitionEvent) => {
      let transcript = '';

      for (const result of recognitionEvent.results) {
        transcript += result[0].transcript;
      }

      input.value = transcript;
      // Native "input" event so predictive-search (and any other listener) picks it up
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    recognition.addEventListener('end', () => {
      button.classList.remove('is-listening');
      button.setAttribute('aria-pressed', 'false');
      form.classList.remove('is-listening');

      if (activeRecognition === recognition) {
        activeRecognition = null;
        activeButton = null;
      }

      if (input.value.trim() !== '') {
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.focus();
      }
    });

    recognition.addEventListener('error', () => {
      button.classList.remove('is-listening');
      button.classList.add('has-error');
      button.setAttribute('aria-pressed', 'false');
      form.classList.remove('is-listening');
      window.setTimeout(() => button.classList.remove('has-error'), 1500);
    });

    try {
      recognition.start();
    } catch (error) {
      button.classList.remove('is-listening');
      form.classList.remove('is-listening');
    }
  };

  document.addEventListener('click', onButtonClick);
})();
